import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { normalizeOrganizationOption } from "../../Adapters/organizationAdapter";
//import useCustomerCardSource from "../../customerCards/useCustomerCardSource";

import {
  applyAddressBaselineToDraft,  
  createInitialDraft,  
  linkOrganizationDraft,
  refreshDraftFromScalarBaseline,
  unlinkOrganizationDraft,
} from "../model/customerCardModel";

import {
  createOrganizationAddressBaseline,
  createOrganizationBaseline,
  createOrganizationScalarBaseline,
  mergeOrganizationOptions,
} from "../model/organizationModel";

const initialBaselineFor = (organization, existingCard) => {
  if (!organization) return null;
  if (existingCard && existingCard.organizationId !== organization.id) return null;
  return createOrganizationBaseline(organization, null);
};

const useCustomerCardDialog = ({  
  existingCard,  
  open,
  organization,
  organizationOptions,
}) => {
  const intl = useIntl();
  const normalizedIncomingOrganization = useMemo(
    () => normalizeOrganizationOption(organization),
    [organization]
  );
  const initialBaseline = initialBaselineFor(
    normalizedIncomingOrganization,
    existingCard
  );

  const [activeStep, setActiveStep] = useState(0);

  const [draft, setDraft] = useState(() =>
    createInitialDraft({
      organization: normalizedIncomingOrganization,
      existingCard,
      baseline: initialBaseline,
    })
  );
  const [logoError, setLogoError] = useState("");

  const previousScalarBaselineRef = useRef(
    initialBaseline
      ? createOrganizationScalarBaseline(normalizedIncomingOrganization)
      : null
  );
  const appliedAddressSourceKeyRef = useRef(null);

  const availableOrganizations = useMemo(
    () =>
      mergeOrganizationOptions(
        normalizedIncomingOrganization,
        organizationOptions
      ),
    [normalizedIncomingOrganization, organizationOptions]
  );

  const selectedOrganization = useMemo(() => {
    const id = draft.organizationId;
    return (
      availableOrganizations.find(candidate => candidate.id === id) ||
      (normalizedIncomingOrganization?.id === id
        ? normalizedIncomingOrganization
        : null)
    );
  }, [availableOrganizations, draft.organizationId, normalizedIncomingOrganization]);

  const {
    organization: sourcedOrganization,
    addressSource,
  } = {};

  // Stable external-data boundary: future GraphQL can replace both scalars and
  // addresses inside useCustomerCardSource without changing this dialog.
  const masterOrganization = sourcedOrganization || selectedOrganization;

  const scalarBaseline = useMemo(
    () => createOrganizationScalarBaseline(masterOrganization),
    [masterOrganization]
  );
  const addressBaseline = useMemo(
    () =>
      createOrganizationAddressBaseline(masterOrganization, addressSource),
    [masterOrganization, addressSource]
  );

  const incomingOrganizationId = normalizedIncomingOrganization?.id ?? null;
  const incomingExistingCardId =
    existingCard?.customerCardId ?? existingCard?.organizationId ?? null;

  /* Reset only on stable dialog/card/organization identity changes. */
  useEffect(() => {
    if (!open) return;

    const resetBaseline = initialBaselineFor(
      normalizedIncomingOrganization,
      existingCard
    );

        setLogoError("");
    previousScalarBaselineRef.current = resetBaseline
      ? createOrganizationScalarBaseline(normalizedIncomingOrganization)
      : null;
    appliedAddressSourceKeyRef.current = null;
  }, [incomingExistingCardId, incomingOrganizationId, open]);

  /*
   * Scalar master refresh. Existing customer overrides are preserved: a value
   * follows master only while it still equals the previous master value.
   * Addresses are intentionally owned by the separate address-source effect.
   */
  useEffect(() => {
    if (!open || !scalarBaseline) {
      if (!masterOrganization) previousScalarBaselineRef.current = null;
      return;
    }

    const previousBaseline = previousScalarBaselineRef.current;

    // If an existing card is resolved before we have any previous master
    // snapshot, establish comparison data without overwriting customer values.
    if (!(existingCard && !previousBaseline)) {
      setDraft(current =>
        refreshDraftFromScalarBaseline(
          current,
          previousBaseline,
          scalarBaseline
        )
      );
    }

    previousScalarBaselineRef.current = scalarBaseline;
  }, [existingCard, masterOrganization, open, scalarBaseline]);

  /*
   * New customer cards use the resolved GraphQL address source as authoritative
   * starting data. Existing cards keep their customer-specific address values;
   * the derived baseline still updates for comparison/reset purposes.
   */
  useEffect(() => {
    if (
      !open ||
      existingCard ||
      !addressSource?.key ||
      !addressBaseline ||
      appliedAddressSourceKeyRef.current === addressSource.key
    ) {
      return;
    }

    setDraft(current => applyAddressBaselineToDraft(current, addressBaseline));
    appliedAddressSourceKeyRef.current = addressSource.key;
  }, [addressBaseline, addressSource, existingCard, open]);


  const handleOrganizationChange = selected => {
    if (!selected) {
      setDraft(current => unlinkOrganizationDraft(current));
      previousScalarBaselineRef.current = null;
      appliedAddressSourceKeyRef.current = null;
      return;
    }

    const normalized = normalizeOrganizationOption(selected);
    const nextBaseline = createOrganizationBaseline(normalized, null);

    setDraft(current => linkOrganizationDraft(current, normalized, nextBaseline));
    appliedAddressSourceKeyRef.current = null;
  };

  const organizationLogo = masterOrganization?.logoUrl || "";
  const logoSrc = draft.logoHidden
    ? ""
    : draft.logoDataUrl || organizationLogo;

  const handleLogoFile = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/svg+xml",
    ];
    if (!allowedTypes.includes(file.type)) {
      setLogoError(intl.formatMessage({ id:"admin.crm.customerCard.logo.invalidType" }));
      return;
    }

    if (file.size > 1024 * 1024) {
      setLogoError(intl.formatMessage({ id:"admin.crm.customerCard.logo.tooLarge" }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setDraft(current => ({
        ...current,
        logoDataUrl: String(reader.result || ""),
        logoHidden: false,
      }));
      setLogoError("");
    };
    reader.onerror = () => setLogoError(intl.formatMessage({ id:"admin.crm.customerCard.logo.readError" }));
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => {
    setDraft(current => ({
      ...current,
      logoDataUrl: "",
      logoHidden: false,
    }));
    setLogoError("");
  };


  return {
    
    availableOrganizations,
    selectedOrganization,
    masterOrganization,

    logoError,
    logoSrc,
    
    
    handleLogoFile,
    handleResetLogo,
    handleOrganizationChange,
    
  };
};

export default useCustomerCardDialog;
